import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore,
  Business,
  Refresh
} from '@mui/icons-material';
import { AccountWithCameras, CameraInfo } from '../api/recordingApi';
import CameraRecordingCard from './CameraRecordingCard';

interface AccountCameraListProps {
  accounts: AccountWithCameras[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  onScheduleSuccess?: () => void;
}

const AccountCameraList: React.FC<AccountCameraListProps> = ({
  accounts,
  loading = false,
  error = null,
  onRefresh,
  onScheduleSuccess
}) => {
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());

  const handleAccountToggle = (accountId: string) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const getTotalCameras = () => {
    return accounts.reduce((total, account) => total + account.cameras.length, 0);
  };

  const getOnlineCameras = () => {
    return accounts.reduce((total, account) => 
      total + account.cameras.filter(camera => camera.status !== 'error').length, 0
    );
  };

  const getAccountStatusSummary = (cameras: CameraInfo[]) => {
    const online = cameras.filter(cam => cam.status === 'idle').length;
    const recording = cameras.filter(cam => cam.status === 'recording').length;
    const error = cameras.filter(cam => cam.status === 'error').length;
    
    return { online, recording, error, total: cameras.length };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Lade Kameras...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (accounts.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Keine Kameras gefunden.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Camera Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {accounts.length} Accounts • {getTotalCameras()} Kameras • {getOnlineCameras()} online
              </Typography>
            </Box>
            {onRefresh && (
              <IconButton onClick={onRefresh} color="primary">
                <Refresh />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Account Accordions */}
      {accounts.map((account) => {
        const statusSummary = getAccountStatusSummary(account.cameras);
        const isExpanded = expandedAccounts.has(account.account_id);

        return (
          <Accordion
            key={account.account_id}
            expanded={isExpanded}
            onChange={() => handleAccountToggle(account.account_id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                <Business sx={{ mr: 2, color: 'primary.main' }} />
                
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div">
                    {account.account_name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {account.subdomain ? `Subdomain: ${account.subdomain}` : 'Fallback subdomain'}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      {statusSummary.total} Kameras
                    </Typography>
                  </Box>
                </Box>

                {/* Status Badges */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {statusSummary.recording > 0 && (
                    <Chip
                      size="small"
                      label={`${statusSummary.recording} recording`}
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {statusSummary.online > 0 && (
                    <Chip
                      size="small"
                      label={`${statusSummary.online} online`}
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {statusSummary.error > 0 && (
                    <Chip
                      size="small"
                      label={`${statusSummary.error} error`}
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Grid container spacing={2}>
                {account.cameras.map((camera) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={camera.id}>
                    <CameraRecordingCard 
                      camera={camera} 
                      onScheduleSuccess={onScheduleSuccess}
                    />
                  </Grid>
                ))}
              </Grid>
              
              {account.cameras.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Keine Kameras für diesen Account gefunden.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default AccountCameraList; 